import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../services/api';

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-[10px] text-anchor-blue hover:text-white transition-colors px-2 py-0.5 rounded border border-anchor-blue/30 hover:border-anchor-blue">
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

const GeneralSettings: React.FC = () => {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user?.tenantId) { setLoading(false); return; }
      try {
        const data = await api.getTenant(user.tenantId);
        setTenant(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenant data');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [user?.tenantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue"></div>
      </div>
    );
  }

  const orgName = tenant?.name || user?.name || 'Data Anchor';
  const tenantId = user?.tenantId || 'Not available';

  const fields = [
    { label: 'Organization Name', value: orgName, editable: true },
    { label: 'Tenant ID', value: tenantId, copy: true },
    { label: 'API Endpoint', value: import.meta.env.VITE_API_URL_PRODUCTION || 'https://app-anchor-api-production.azurewebsites.net', copy: true },
    { label: 'Dashboard URL', value: 'https://www.dataanchor.io', copy: true },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300">
          {error} — showing cached data
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-5">Organization</h3>
        <div className="space-y-4">
          {fields.map((field) => (
            <div key={field.label} className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-b-0">
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider mb-1">{field.label}</div>
                <div className="text-sm text-white/80 font-mono">{field.value}</div>
              </div>
              <div className="flex items-center gap-2">
                {field.copy && <CopyButton value={field.value} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-5">Your Account</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-white/[0.04]">
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wider mb-1">Name</div>
              <div className="text-sm text-white/80">{user?.name || 'Unknown'}</div>
            </div>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-white/[0.04]">
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wider mb-1">Email</div>
              <div className="text-sm text-white/80 font-mono">{user?.email || 'Unknown'}</div>
            </div>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wider mb-1">Role</div>
              <div className="text-sm text-white/80">Administrator</div>
            </div>
            <span className="text-[10px] py-1 px-2.5 rounded-full bg-anchor-teal/10 text-anchor-teal border border-anchor-teal/20">
              Admin
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
